import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import '../../components/tracker/Tracker.css';
import '../../components/tracker/PlayerDashboard.css';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function StatCell({ label, value }) {
  return (
    <div className="detail-stat-cell">
      <div className="detail-stat-value">{value ?? '—'}</div>
      <div className="detail-stat-label">{label}</div>
    </div>
  );
}

function EditStatCell({ label, value, onChange }) {
  return (
    <div className="detail-stat-cell">
      <input
        type="number"
        value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className="detail-edit-input"
        min="0"
        step="1"
      />
      <div className="detail-stat-label">{label}</div>
    </div>
  );
}

function SectionHeader({ children, editMode, onEdit }) {
  return (
    <div className="detail-section-header">
      <span>{children}</span>
    </div>
  );
}

export default function GameDetailPage() {
  const navigate = useNavigate();
  const { id, gameId } = useParams();
  const [game, setGame] = useState(null);
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const [playerRes, gameRes] = await Promise.all([
          api.get(`/analytics/players/${id}`),
          api.get(`/analytics/players/${id}/games/${gameId}`),
        ]);
        setPlayer(playerRes.data);
        setGame(gameRes.data);
      } catch (err) {
        console.error('Failed to load game:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id, gameId]);

  function enterEditMode() {
    setEditData({
      gameDate: game.gameDate,
      opponent: game.opponent || '',
      notes: game.notes || '',
      gameType: game.gameType || '',
      homeAway: game.homeAway || '',
      teamScore: game.teamScore ?? '',
      opponentScore: game.opponentScore ?? '',
      venue: game.venue || '',
      pitching: game.pitching ? { ...game.pitching } : null,
      batting: game.batting ? { ...game.batting } : null,
      fielding: game.fielding ? game.fielding.map(p => ({ ...p })) : null,
    });
    setEditMode(true);
    setSaveError(null);
  }

  function cancelEdit() {
    setEditMode(false);
    setEditData(null);
    setSaveError(null);
  }

  function updateMeta(field, value) {
    setEditData(prev => ({ ...prev, [field]: value }));
  }

  function updatePitching(field, value) {
    setEditData(prev => ({
      ...prev,
      pitching: { ...prev.pitching, [field]: value },
    }));
  }

  function updateBatting(field, value) {
    setEditData(prev => ({
      ...prev,
      batting: { ...prev.batting, [field]: value },
    }));
  }

  function updateFielding(index, field, value) {
    setEditData(prev => {
      const updated = prev.fielding.map((p, i) =>
        i === index ? { ...p, [field]: value } : p
      );
      return { ...prev, fielding: updated };
    });
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await api.put(`/analytics/players/${id}/games/${gameId}`, {
        gameDate: editData.gameDate,
        opponent: editData.opponent,
        notes: editData.notes,
        gameType: editData.gameType || null,
        homeAway: editData.homeAway || null,
        teamScore: editData.teamScore !== '' ? Number(editData.teamScore) : null,
        opponentScore: editData.opponentScore !== '' ? Number(editData.opponentScore) : null,
        venue: editData.venue || null,
        pitching: editData.pitching,
        batting: editData.batting,
        fielding: editData.fielding,
      });
      setGame(res.data);
      setEditMode(false);
      setEditData(null);
    } catch (err) {
      console.error('Failed to save game:', err);
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="dash-page">
        <div className="analytics-page-wrap">
          <div className="analytics-empty">Loading…</div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="dash-page">
        <div className="analytics-page-wrap">
          <div className="analytics-empty">Game not found.</div>
        </div>
      </div>
    );
  }

  const p = editMode ? editData.pitching : game.pitching;
  const b = editMode ? editData.batting : game.batting;
  const f = editMode ? editData.fielding : game.fielding;

  const avg =
    b && b.ab > 0
      ? (b.h / b.ab).toFixed(3).replace(/^0/, '')
      : b?.ab === 0
        ? '.000'
        : '—';
  const obpNum = b ? b.h + (b.bb || 0) + (b.hbp || 0) : 0;
  const obpDen = b ? b.ab + (b.bb || 0) + (b.hbp || 0) + (b.sf || 0) : 0;
  const obp = obpDen > 0 ? (obpNum / obpDen).toFixed(3).replace(/^0/, '') : '—';
  const tb = b
    ? (b['1b'] || 0) + 2 * (b['2b'] || 0) + 3 * (b['3b'] || 0) + 4 * (b.hr || 0)
    : 0;
  const slg = b && b.ab > 0 ? (tb / b.ab).toFixed(3).replace(/^0/, '') : '—';

  return (
    <div className="dash-page">
      <div className="analytics-page-wrap">
        {/* Header */}
        <div className="analytics-page-header">
          <button
            className="analytics-back-btn"
            onClick={() => !editMode && navigate(`/analytics/players/${id}/games`)}
            style={editMode ? { visibility: 'hidden' } : {}}
          >
            ← History
          </button>
          <h1 className="analytics-page-title">Game Detail</h1>
          {!editMode ? (
            <button className="detail-edit-btn" onClick={enterEditMode}>
              Edit
            </button>
          ) : (
            <div style={{ width: '80px' }} />
          )}
        </div>

        {/* Game meta */}
        <div className="detail-meta-card">
          <div className="detail-meta-player">
            {player ? `${player.firstName} ${player.lastName}` : ''}
          </div>
          {editMode ? (
            <div className="detail-edit-meta-form">
              <div className="detail-edit-field">
                <label className="detail-edit-label">Opponent</label>
                <input
                  type="text"
                  value={editData.opponent}
                  onChange={e => updateMeta('opponent', e.target.value)}
                  className="detail-edit-text-input"
                  placeholder="Opponent name"
                />
              </div>
              <div className="detail-edit-field">
                <label className="detail-edit-label">Date</label>
                <input
                  type="date"
                  value={editData.gameDate}
                  onChange={e => updateMeta('gameDate', e.target.value)}
                  className="detail-edit-text-input"
                />
              </div>
              <div className="detail-edit-field">
                <label className="detail-edit-label">Game Type</label>
                <select
                  value={editData.gameType}
                  onChange={e => updateMeta('gameType', e.target.value)}
                  className="detail-edit-text-input"
                >
                  <option value="">Select...</option>
                  <option value="Regular Season">Regular Season</option>
                  <option value="Tournament">Tournament</option>
                  <option value="Playoff">Playoff</option>
                  <option value="Scrimmage">Scrimmage</option>
                  <option value="Fall Ball">Fall Ball</option>
                </select>
              </div>
              <div className="detail-edit-field">
                <label className="detail-edit-label">Home / Away</label>
                <select
                  value={editData.homeAway}
                  onChange={e => updateMeta('homeAway', e.target.value)}
                  className="detail-edit-text-input"
                >
                  <option value="">Select...</option>
                  <option value="home">Home</option>
                  <option value="away">Away</option>
                </select>
              </div>
              <div className="detail-edit-field">
                <label className="detail-edit-label">Score</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="number"
                    value={editData.teamScore}
                    onChange={e => updateMeta('teamScore', e.target.value)}
                    className="detail-edit-text-input"
                    placeholder="Us"
                    min="0"
                    style={{ width: '80px' }}
                  />
                  <span style={{ color: '#4a5568', fontWeight: 700 }}>–</span>
                  <input
                    type="number"
                    value={editData.opponentScore}
                    onChange={e => updateMeta('opponentScore', e.target.value)}
                    className="detail-edit-text-input"
                    placeholder="Them"
                    min="0"
                    style={{ width: '80px' }}
                  />
                </div>
              </div>
              <div className="detail-edit-field">
                <label className="detail-edit-label">Venue</label>
                <input
                  type="text"
                  value={editData.venue}
                  onChange={e => updateMeta('venue', e.target.value)}
                  className="detail-edit-text-input"
                  placeholder="Field or park name"
                />
              </div>
              <div className="detail-edit-field">
                <label className="detail-edit-label">Notes</label>
                <textarea
                  value={editData.notes}
                  onChange={e => updateMeta('notes', e.target.value)}
                  className="detail-edit-text-input"
                  rows={3}
                  placeholder="Game notes..."
                />
              </div>
            </div>
          ) : (
            <>
              <div className="detail-meta-matchup">vs {game.opponent || '—'}</div>
              <div className="detail-meta-row">
                <span>{formatDate(game.gameDate)}</span>
                {game.gameType && <span className="detail-meta-dot">·</span>}
                {game.gameType && <span>{game.gameType}</span>}
                {game.homeAway && <span className="detail-meta-dot">·</span>}
                {game.homeAway && (
                  <span className={`detail-home-away ${game.homeAway}`}>
                    {game.homeAway === 'home' ? '🏠 Home' : '✈️ Away'}
                  </span>
                )}
              </div>
              {(game.teamScore != null || game.opponentScore != null) && (
                <div className="detail-score">
                  {game.teamScore ?? '—'} – {game.opponentScore ?? '—'}
                </div>
              )}
              {game.venue && <div className="detail-venue">{game.venue}</div>}
              {game.notes && <div className="detail-notes">{game.notes}</div>}
            </>
          )}
        </div>

        {/* Pitching */}
        {p && (
          <div className="detail-section">
            <SectionHeader>Pitching</SectionHeader>
            {editMode ? (
              <div className="detail-stat-grid">
                <EditStatCell label="IP" value={p.ip} onChange={v => updatePitching('ip', v)} />
                <EditStatCell label="K" value={p.k} onChange={v => updatePitching('k', v)} />
                <EditStatCell label="BB" value={p.bb} onChange={v => updatePitching('bb', v)} />
                <EditStatCell label="H" value={p.h} onChange={v => updatePitching('h', v)} />
                <EditStatCell label="R" value={p.r} onChange={v => updatePitching('r', v)} />
                <EditStatCell label="ER" value={p.er} onChange={v => updatePitching('er', v)} />
                <EditStatCell label="HR" value={p.hr} onChange={v => updatePitching('hr', v)} />
                <EditStatCell label="HBP" value={p.hbp} onChange={v => updatePitching('hbp', v)} />
                <EditStatCell label="WP" value={p.wp} onChange={v => updatePitching('wp', v)} />
                <EditStatCell label="Pitches" value={p.pitches} onChange={v => updatePitching('pitches', v)} />
              </div>
            ) : (
              <div className="detail-stat-grid">
                <StatCell label="IP" value={p.ip} />
                <StatCell label="K" value={p.k} />
                <StatCell label="BB" value={p.bb} />
                <StatCell label="H" value={p.h} />
                <StatCell label="R" value={p.r} />
                <StatCell label="ER" value={p.er} />
                <StatCell label="HR" value={p.hr} />
                <StatCell label="HBP" value={p.hbp} />
                <StatCell label="WP" value={p.wp} />
                <StatCell label="Pitches" value={p.pitches} />
              </div>
            )}
            {!editMode && (p.avg_velo || p.max_velo) && (
              <div className="detail-sub-row">
                {p.avg_velo && <span>Avg Velo: <strong>{p.avg_velo} mph</strong></span>}
                {p.max_velo && <span>Max Velo: <strong>{p.max_velo} mph</strong></span>}
              </div>
            )}
            {!editMode && p.first_pitch_strikes != null && (
              <div className="detail-sub-row">
                <span>
                  1st Pitch Strikes:{' '}
                  <strong>
                    {p.first_pitch_strikes}/
                    {(p.first_pitch_strikes || 0) + (p.first_pitch_balls || 0)}{' '}
                    ({p.first_pitch_strikes + p.first_pitch_balls > 0
                      ? Math.round((p.first_pitch_strikes / (p.first_pitch_strikes + p.first_pitch_balls)) * 100)
                      : 0}%)
                  </strong>
                </span>
              </div>
            )}
            {!editMode && p.role && (
              <div className="detail-sub-row">
                <span>Role: <strong>{p.role}</strong></span>
                {p.decision && <span>Decision: <strong>{p.decision}</strong></span>}
              </div>
            )}
          </div>
        )}

        {/* Batting */}
        {b && (
          <div className="detail-section">
            <SectionHeader>Batting</SectionHeader>
            {!editMode && (
              <div className="detail-rate-row">
                <div className="detail-rate-cell">
                  <div className="detail-rate-value">{avg}</div>
                  <div className="detail-rate-label">AVG</div>
                </div>
                <div className="detail-rate-cell">
                  <div className="detail-rate-value">{obp}</div>
                  <div className="detail-rate-label">OBP</div>
                </div>
                <div className="detail-rate-cell">
                  <div className="detail-rate-value">{slg}</div>
                  <div className="detail-rate-label">SLG</div>
                </div>
              </div>
            )}
            {editMode ? (
              <div className="detail-stat-grid">
                <EditStatCell label="PA" value={b.pa} onChange={v => updateBatting('pa', v)} />
                <EditStatCell label="AB" value={b.ab} onChange={v => updateBatting('ab', v)} />
                <EditStatCell label="H" value={b.h} onChange={v => updateBatting('h', v)} />
                <EditStatCell label="R" value={b.r} onChange={v => updateBatting('r', v)} />
                <EditStatCell label="RBI" value={b.rbi} onChange={v => updateBatting('rbi', v)} />
                <EditStatCell label="BB" value={b.bb} onChange={v => updateBatting('bb', v)} />
                <EditStatCell label="K" value={b.k} onChange={v => updateBatting('k', v)} />
                <EditStatCell label="HR" value={b.hr} onChange={v => updateBatting('hr', v)} />
                <EditStatCell label="2B" value={b['2b']} onChange={v => updateBatting('2b', v)} />
                <EditStatCell label="3B" value={b['3b']} onChange={v => updateBatting('3b', v)} />
                <EditStatCell label="SB" value={b.sb} onChange={v => updateBatting('sb', v)} />
                <EditStatCell label="CS" value={b.cs} onChange={v => updateBatting('cs', v)} />
              </div>
            ) : (
              <div className="detail-stat-grid">
                <StatCell label="PA" value={b.pa} />
                <StatCell label="AB" value={b.ab} />
                <StatCell label="H" value={b.h} />
                <StatCell label="R" value={b.r} />
                <StatCell label="RBI" value={b.rbi} />
                <StatCell label="BB" value={b.bb} />
                <StatCell label="K" value={b.k} />
                <StatCell label="HR" value={b.hr} />
                <StatCell label="2B" value={b['2b']} />
                <StatCell label="3B" value={b['3b']} />
                <StatCell label="SB" value={b.sb} />
                <StatCell label="CS" value={b.cs} />
              </div>
            )}
            {!editMode && b.ab_risp != null && (
              <div className="detail-sub-row">
                <span>RISP: <strong>{b.h_risp}/{b.ab_risp}</strong></span>
                {b.rbi_risp > 0 && <span>RBI w/RISP: <strong>{b.rbi_risp}</strong></span>}
              </div>
            )}
          </div>
        )}

        {/* Fielding */}
        {f && Array.isArray(f) && f.length > 0 && (
          <div className="detail-section">
            <SectionHeader>Fielding</SectionHeader>
            {f.map((pos, i) => {
              const tc = (pos.po || 0) + (pos.a || 0) + (pos.e || 0);
              const fpct =
                tc > 0
                  ? ((pos.po + pos.a) / tc).toFixed(3).replace(/^0/, '')
                  : '1.000';
              return (
                <div key={i} className="detail-fielding-row">
                  <div className="detail-fielding-pos">{pos.position}</div>
                  {editMode ? (
                    <div className="detail-fielding-edit-grid">
                      <EditStatCell label="PO" value={pos.po} onChange={v => updateFielding(i, 'po', v)} />
                      <EditStatCell label="A" value={pos.a} onChange={v => updateFielding(i, 'a', v)} />
                      <EditStatCell label="E" value={pos.e} onChange={v => updateFielding(i, 'e', v)} />
                      <EditStatCell label="DP" value={pos.dp} onChange={v => updateFielding(i, 'dp', v)} />
                      {pos.position === 'C' && (
                        <>
                          <EditStatCell label="CS" value={pos.caught_stealing} onChange={v => updateFielding(i, 'caught_stealing', v)} />
                          <EditStatCell label="PB" value={pos.passed_balls} onChange={v => updateFielding(i, 'passed_balls', v)} />
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="detail-fielding-stats">
                      {pos.innings > 0 && <span>{pos.innings} Inn</span>}
                      <span>{pos.po} PO</span>
                      <span>{pos.a} A</span>
                      <span className={pos.e > 0 ? 'detail-error' : ''}>{pos.e} E</span>
                      {pos.dp > 0 && <span>{pos.dp} DP</span>}
                      <span>{fpct} FPCT</span>
                      {pos.position === 'C' && pos.caught_stealing != null && (
                        <span>{pos.caught_stealing} CS</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Edit mode actions */}
        {editMode && (
          <div className="detail-edit-actions">
            {saveError && <div className="detail-edit-error">{saveError}</div>}
            <button
              className="detail-save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              className="detail-cancel-btn"
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
